import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEOHead from "@/components/SEOHead";
import {
  LocationChoiceBlock,
  LocationFaqs,
  LocationFinalCta,
  LocationProofStrip,
  LocationSolutionsBlock,
  LocationSupportAndReviews,
} from "@/components/LocationSharedBlocks";
import { HUB, plainText } from "@/data/locationsContent";
import { fadeUp, stagger } from "@/utils/motionVariants";

/**
 * /locations — North West hub (new in handover v4, Sep 2026).
 *
 * Section order is fixed by the handover:
 *   hero + North West intro → five-area finder → two customer-choice cards →
 *   proof strip → not-listed enquiry link → six solutions → local support +
 *   review link → six hub FAQs → final CTA.
 *
 * No hub form and no separate hero button row, by design: the choice cards
 * after the finder carry the two journeys. Copy lives in
 * data/locationsContent.js.
 */

export default function LocationsHubPage() {
  return (
    <div data-testid="locations-hub-page">
      <SEOHead
        rawTitle={HUB.seoTitle}
        description={HUB.metaDescription}
        path={HUB.path}
        faqs={HUB.faqs.map((f) => ({ q: f.q, a: plainText(f.a) }))}
        service={{
          name: "Card machines and payment support in the North West",
          description: HUB.metaDescription,
          serviceType: "Merchant Services",
          areaServed: HUB.areaServed,
        }}
      />

      {/* 1. Hero + North West introduction */}
      <section className="hero-gradient py-16 md:py-24" data-testid="locations-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <Breadcrumbs className="mb-5" />
            <motion.p
              variants={fadeUp}
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-deep mb-4"
            >
              <MapPin size={12} aria-hidden="true" />
              {HUB.eyebrow}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-black tracking-tight leading-tight mb-6"
            >
              {HUB.h1}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey-dark max-w-3xl leading-relaxed">
              {HUB.heroBody}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* 2. Five-area finder */}
      <section className="py-14 md:py-16 bg-white" data-testid="locations-finder">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-deep mb-3">{HUB.finder.eyebrow}</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-3">{HUB.finder.heading}</h2>
            <p className="text-sm text-brand-grey-dark leading-relaxed">{HUB.finder.intro}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {HUB.finder.areas.map((area) => (
              <Link
                key={area.slug}
                to={`/locations/${area.slug}`}
                className="group bg-white border border-brand-light-grey rounded-2xl p-6 hover:border-brand-aqua/50 hover:shadow-md transition-all"
                data-testid={`locations-finder-${area.slug}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-brand-aqua-light flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-brand-aqua-deep" aria-hidden="true" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-lg font-bold text-brand-black mb-1 flex items-center gap-1.5">
                      {area.label}
                      <ArrowRight size={14} className="text-brand-aqua opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
                    </h3>
                    <p className="text-sm text-brand-grey-dark leading-relaxed">{area.body}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Two customer-choice cards */}
      <LocationChoiceBlock testId="locations-choice" />

      {/* 4. Qualified proof strip */}
      <LocationProofStrip testId="locations-proof-strip" />

      {/* 5. Not-listed area enquiry link */}
      <section className="py-14 md:py-16 bg-white" data-testid="locations-not-listed">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-brand-aqua-light/60 border border-brand-aqua/20 rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="max-w-2xl">
              <h2 className="font-heading text-2xl font-bold text-brand-black tracking-tight mb-2">{HUB.notListed.heading}</h2>
              <p className="text-sm text-brand-grey-dark leading-relaxed">{HUB.notListed.body}</p>
            </div>
            <Link
              to={HUB.notListed.cta.to}
              className="bg-brand-aqua-deep hover:bg-brand-aqua-deeper text-white font-semibold rounded-full px-7 py-3.5 text-sm inline-flex items-center justify-center gap-2 transition-colors btn-shine flex-shrink-0"
              data-testid="locations-not-listed-cta"
            >
              {HUB.notListed.cta.label}<ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* 6. Six payment-solution cards */}
      <LocationSolutionsBlock locationName="North West" testId="locations-solutions" />

      {/* 7 + 8. Local support and customer-review link */}
      <LocationSupportAndReviews testId="locations-support" />

      {/* 9. Six hub FAQs */}
      <LocationFaqs heading="Locations FAQs" faqs={HUB.faqs} testIdPrefix="locations-faq" />

      {/* 10. Final two actions and online helper */}
      <LocationFinalCta testId="locations-final-cta" />
    </div>
  );
}
